import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { supabase, signOut } from '../lib/supabase'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { BillingManagement } from '../components/BillingManagement'

// ============================================================================
// Page: /dashboard/team/:teamId/billing
//
// Billing & Plan, split out of the old Team Management modal
// (TeamManagement/TeamSettings.tsx's "Billing & Plan" tab) into its own
// sidebar page — same decision/rationale as TeamConfig.tsx. Renders
// BillingManagement in its default inline mode (isOpen left false, matching
// how TeamSettings.tsx already used it — isOpen=true is a separate modal
// variant of the same component, not needed here).
// ============================================================================

interface TeamBillingProps {
  teamId: string
}

type TeamRole = 'owner' | 'admin' | 'member'

const TeamBilling: React.FC<TeamBillingProps> = ({ teamId }) => {
  const navigate = useNavigate()
  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const [userRole, setUserRole] = useState<TeamRole>('member')

  useEffect(() => {
    let cancelled = false
    supabase
      .from('teams')
      .select('name')
      .eq('id', teamId)
      .single()
      .then(({ data }) => {
        if (!cancelled) setTeamName(data?.name ?? undefined)
      })
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      supabase
        .from('team_members')
        .select('role')
        .eq('team_id', teamId)
        .eq('auth_user_id', user.id)
        .single()
        .then(({ data }) => {
          if (!cancelled && data?.role) setUserRole(data.role as TeamRole)
        })
    })
    return () => {
      cancelled = true
    }
  }, [teamId])

  const goToDashboard = () => navigate(`/dashboard/team/${teamId}`)

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  return (
    <SidebarProvider>
      <AppSidebar activeSection="billing" onNavigate={goToDashboard} teamId={teamId} />
      <SidebarInset>
        <TopBar teamId={teamId} teamName={teamName} onLogout={handleLogout} sectionLabel="Billing" />
        <main className="space-y-6 bg-background p-4 sm:p-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground mb-1">
              {teamName ? `${teamName} Billing` : 'Team Billing'}
            </h1>
            <p className="text-muted-foreground text-sm">Plan, usage, and payment details.</p>
          </div>

          <BillingManagement teamId={teamId} userRole={userRole} />
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default TeamBilling
